import { createSlice } from '@reduxjs/toolkit';
import { SearchV2Request, SearchV2Results, SearchV2State } from '../interface/search-v2.interface';

/*----- CREATE STORE ------*/
declare interface SearchV2Slice {
  name: string;
  initialState: SearchV2State;
  reducers: any;
}

const slice: SearchV2Slice = {
  name: 'searchV2',
  initialState: {
    search: {
      data: undefined,
      request: undefined,
      isLoading: false,
      isLoaded: false,
      isSuccess: false,
      isFailure: false,
      message: 'No search performed 1',
    },
    facetLabels: undefined,
  },
  reducers: {
    onSuccess: (state: SearchV2State, { payload }: { payload: SearchV2Results }) => {
      state.search = {
        data: payload,
        request: state?.search?.request ? removeUnavailableFacetsForSearchV2(state.search.request, payload) : undefined,
        isLoaded: true,
        isLoading: false,
        isSuccess: true,
        isFailure: false,
        message: `Successfully loaded search`,
      };
    },
    onFailure: (state: SearchV2State) => {
      state.search = {
        data: undefined,
        request: state?.search?.request,
        isLoaded: false,
        isLoading: false,
        isSuccess: false,
        isFailure: true,
        message: `Failed to load search`,
      };
    },
    setLoading: (state: SearchV2State, action: any) => {
      state.search = {
        data: state?.search?.data,
        request: action.payload,
        isLoaded: state?.search?.isLoaded,
        isLoading: true,
        isSuccess: false,
        isFailure: state?.search?.isFailure,
        message: `Loading search results`,
      };
    },
    setSearchTerm: (state: SearchV2State, action: any) => {
      if (!state.search.request) {
        state.search.request = {
          searchTerms: action.payload,
          pageLength: 0,
          start: 0,
          facets: false,
        };
      } else {
        state.search.request.searchTerms = action.payload;
      }
    },
    setFacetLabels: (state: SearchV2State, action: any) => {
      state.facetLabels = action.payload;
    },
  },
};

export const searchV2Slice: any = createSlice(slice);

/*----- EXPORT ACTIONS ------*/
export const onSuccess: Function = searchV2Slice.actions.onSuccess;
export const onFailure: Function = searchV2Slice.actions.onFailure;
export const setLoading: Function = searchV2Slice.actions.setLoading;
export const setSearchTerm: Function = searchV2Slice.actions.setSearchTerm;
export const setFacetLabels: Function = searchV2Slice.actions.setFacetLabels;

/*----- CREATE REDUCER ------*/

export const searchV2Reducer = searchV2Slice.reducer;

/**
 * @param request The request that might have invalid filters.
 * @param results The results of the request.
 * @returns A new request with any invalid filters removed.
 */
function removeUnavailableFacetsForSearchV2(
  request: SearchV2Request,
  results: SearchV2Results
): SearchV2Request | undefined {
  const filters = request.fieldValueFilter;
  Object.keys(filters || {}).forEach((key) => {
    const facet = results.facets.find((facet) => facet.name === key);
    const values: Array<string> = [];

    facet?.facetValues.forEach((value) => {
      values.push(value.name);
      value.children?.forEach((child) => {
        values.push(child.name);
      });
    });

    if (filters?.[key]) {
      filters[key] = filters[key].filter((filterVal) => values.includes(filterVal));
      // If no values remain after filtering, delete the key entirely to prevent
      // sending malformed requests like { fieldValueFilter: { CONTENT_TAG_PATH: [] } }
      // which the backend rejects with 400 "both facet name and facet values names are required".
      if (filters[key].length === 0) {
        delete filters[key];
      }
    }
  });

  request.fieldValueFilter = filters;
  return request;
}
